import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFirestore, collection, query, where, getDocs, orderBy, limit, startAfter } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { format } from 'date-fns';
import { getPrintJobStatus } from '../services/printService/luluApi';

const PrintJobsPage = () => {
  const [printJobs, setPrintJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailedJobs, setDetailedJobs] = useState({});
  const [loadingDetails, setLoadingDetails] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [uniqueStatuses, setUniqueStatuses] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 5;
  const [page, setPage] = useState(1);
  const [pageCursors, setPageCursors] = useState([null]);

  // Helper function to determine effective status
  const getEffectiveStatus = (job) => {
    if (!job) return { name: 'UNKNOWN', message: '' };
    
    // If we're still loading details for this job, show loading status
    const jobId = job?.printJobId || job.id;
    if (loadingDetails[jobId]) {
      return {
        name: 'LOADING',
        message: 'LOADING...',
      };
    }
    
    const isPaidInApp = job.stripePaid || 
                   job.paymentStatus === 'paid' || 
                   job.paymentStatus === 'PAID_AWAITING_FULFILLMENT' || 
                   job.customerPaymentStatus === 'paid' ||
                   job.status === 'paid' ||
                   job.details?.stripePaymentStatus === 'PAID';
    
    // Check if the print job is paid/in production in Lulu
    // Common Lulu production statuses: CREATED, PAYMENT_RECEIVED, IN_PRODUCTION, etc.
    const isPaidInLulu = job.details?.status?.name && 
                      ['CREATED', 'PAYMENT_RECEIVED', 'IN_PRODUCTION', 'MANUFACTURING'].includes(
                        job.details.status.name.toUpperCase());
                        
    // Check if the job is in PRODUCTION_DELAYED status specifically
    const isProductionDelayed = job.details?.status?.name && 
                            job.details.status.name.toUpperCase() === 'PRODUCTION_DELAYED';
    
    // Check if the print job has been shipped based on status
    // Only mark as shipped if the status explicitly indicates shipping
    const isShipped = job.details?.status?.name === 'SHIPPED' || 
                      job.status === 'SHIPPED';
    
    // If job is both paid and shipped, show it as completed
    if (isPaidInApp && isShipped) {
      return {
        name: 'SHIPPED',
        message: 'ORDER COMPLETED - YOUR PACKAGE HAS BEEN SHIPPED'
      };
    }
    
    // If paid in our app and the status is PRODUCTION_DELAYED in Lulu
    if (isPaidInApp && isProductionDelayed) {
      return {
        name: 'PAID',
        message: 'PAID - PRINT PRODUCTION DELAYED - AWAITING PRINTER'
      };
    }
    
    // If paid in our app and also in Lulu (in production)
    if (isPaidInApp && isPaidInLulu) {
      return {
        name: 'PAID',
        message: 'PAID - ORDER SUBMITTED TO PRINTER - IN PRODUCTION'
      };
    }
    
    // If we have a stripePaid flag or paymentStatus indicating payment, prioritize that
    if (isPaidInApp) {
      return {
        name: 'PAID',
        message: 'PAYMENT RECEIVED - PREPARING YOUR ORDER'
      };
    }
    
    // For shipped jobs
    if (isShipped) {
      return {
        name: 'SHIPPED',
        message: 'ORDER COMPLETED - YOUR PACKAGE HAS BEEN SHIPPED'
      };
    }
    
    // Otherwise use the Lulu API status if available
    if (job.details?.status?.name) {
      return job.details.status;
    }
    
    // Fallback to our local status
    return {
      name: job.status || job.paymentStatus || 'UNKNOWN',
      message: ''
    };
  };

  // No longer needed as we're using a separate page for details

  // Function to fetch the latest Lulu status directly from the API
  const fetchLatestLuluStatus = async (printJobId) => {
    try {
      console.log('Fetching latest Lulu status for job ID:', printJobId);
      const luluStatusResult = await getPrintJobStatus(printJobId);
      return luluStatusResult.data;
    } catch (err) {
      console.error('Error fetching latest Lulu status:', err);
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchPrintJobs = async () => {
      setLoading(true);
      const db = getFirestore();
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setPrintJobs([]);
        setLoading(false);
        return;
      }
      const q = query(
        collection(db, 'printJobs'),
        where('userId', '==', user.uid),
        orderBy('updatedAt', 'desc'),
        limit(PAGE_SIZE)
      );
      const querySnapshot = await getDocs(q);
      /**
       * @typedef {Object} PrintJob
       * @property {string} id - Document ID
       * @property {string} [printJobId] - Print job ID from Lulu API (optional)
       * @property {string} [bookId] - Book ID
       * @property {string} [status] - Status of the print job
       * @property {Object} [createdAt] - Firebase timestamp for creation time
       * @property {Object} [updatedAt] - Firebase timestamp for last update time
       */
      /** @type {PrintJob[]} */
      const jobs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPrintJobs(jobs);
      setLoading(false);
      setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1] || null);
      setHasMore(querySnapshot.docs.length === PAGE_SIZE);
      setPage(1);
      setPageCursors([null, querySnapshot.docs[querySnapshot.docs.length - 1] || null]);

      const NETLIFY_FUNCTION_URL = "/.netlify/functions/lulu-print-job-details";
      const newLoadingState = {};
      jobs.forEach(job => {
        const jobId = job?.printJobId || job.id;
        newLoadingState[jobId] = true;
      });
      setLoadingDetails(newLoadingState);

      for (const job of jobs) {
        if (cancelled) break;
        const printJobId = job?.printJobId || job.id;
        if (printJobId) {
          try {
            const res = await fetch(NETLIFY_FUNCTION_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ printJobId })
            });
            if (cancelled) break;
            if (res.ok) {
              const data = await res.json();
              console.log(`Print job details for ${printJobId}:`, data);
              if (data && data.detail && data.detail === "No PrintJob matches the given query.") {
                setDetailedJobs(prev => ({
                  ...prev,
                  [printJobId]: {
                    status: { name: 'UNKNOWN' },
                    line_items: [],
                  }
                }));
                setLoadingDetails(prev => ({
                  ...prev,
                  [printJobId]: false
                }));
              } else {
                setDetailedJobs(prev => {
                  const updatedJobs = {
                    ...prev,
                    [printJobId]: data
                  };
                  const statusSet = new Set(['all']);
                  Object.values(updatedJobs).forEach(job => {
                    if (job?.status?.name) {
                      statusSet.add(job.status.name);
                    }
                  });
                  setUniqueStatuses(Array.from(statusSet));
                  return updatedJobs;
                });
                setLoadingDetails(prev => ({
                  ...prev,
                  [printJobId]: false
                }));
              }
            }
          } catch (err) {
            console.error(`Error fetching details for ${printJobId}:`, err);
            setLoadingDetails(prev => ({
              ...prev,
              [printJobId]: false
            }));
          }
        } else {
          setLoadingDetails(prev => ({
            ...prev,
            [job.id]: false
          }));
        }
      }
    };
    fetchPrintJobs();
    return () => { cancelled = true; };
  }, []);

  const fetchPage = async (startAfterCursor, targetPage) => {
    setLoadingMore(true);
    const db = getFirestore();
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) { setLoadingMore(false); return; }
    const q = startAfterCursor
      ? query(
          collection(db, 'printJobs'),
          where('userId', '==', user.uid),
          orderBy('updatedAt', 'desc'),
          startAfter(startAfterCursor),
          limit(PAGE_SIZE)
        )
      : query(
          collection(db, 'printJobs'),
          where('userId', '==', user.uid),
          orderBy('updatedAt', 'desc'),
          limit(PAGE_SIZE)
        );
    const querySnapshot = await getDocs(q);
    const jobs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setPrintJobs(jobs);
    const newLast = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
    setLastVisible(newLast);
    setHasMore(querySnapshot.docs.length === PAGE_SIZE);
    setPage(targetPage);
    setPageCursors(prev => {
      const next = [...prev];
      next[targetPage] = newLast;
      return next;
    });

    // Reset details/loading for the new page
    setDetailedJobs({});
    const NETLIFY_FUNCTION_URL = "/.netlify/functions/lulu-print-job-details";
    const newLoadingState = {};
    jobs.forEach(job => {
      const jobId = job?.printJobId || job.id;
      newLoadingState[jobId] = true;
    });
    setLoadingDetails(newLoadingState);

    for (const job of jobs) {
      const printJobId = job?.printJobId || job.id;
      if (printJobId) {
        try {
          const res = await fetch(NETLIFY_FUNCTION_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ printJobId })
          });
          if (res.ok) {
            const data = await res.json();
            if (data && data.detail && data.detail === 'No PrintJob matches the given query.') {
              setDetailedJobs(prev => ({
                ...prev,
                [printJobId]: { status: { name: 'UNKNOWN' }, line_items: [] }
              }));
              setLoadingDetails(prev => ({ ...prev, [printJobId]: false }));
            } else {
              setDetailedJobs(prev => {
                const updatedJobs = { ...prev, [printJobId]: data };
                const statusSet = new Set(['all']);
                Object.values(updatedJobs).forEach(job => {
                  if (job?.status?.name) statusSet.add(job.status.name);
                });
                setUniqueStatuses(Array.from(statusSet));
                return updatedJobs;
              });
              setLoadingDetails(prev => ({ ...prev, [printJobId]: false }));
            }
          }
        } catch (err) {
          console.error(`Error fetching details for ${printJobId}:`, err);
          setLoadingDetails(prev => ({ ...prev, [printJobId]: false }));
        }
      } else {
        setLoadingDetails(prev => ({ ...prev, [job.id]: false }));
      }
    }
    setLoadingMore(false);
  };

  const handleNextPage = async () => {
    if (!hasMore || loadingMore) return;
    const cursor = pageCursors[page] || lastVisible;
    await fetchPage(cursor, page + 1);
  };

  const handlePrevPage = async () => {
    if (page <= 1 || loadingMore) return;
    const prevCursor = pageCursors[page - 2] || null; // null means first page
    await fetchPage(prevCursor, page - 1);
  };

  // Filter jobs based on selected status
  const filteredJobs = printJobs.filter(job => {
    if (statusFilter === 'all') return true;
    const jobStatus = detailedJobs[job?.printJobId || job.id]?.status?.name || job.status;
    return jobStatus === statusFilter;
  });

  return (
    <div className="max-w-6xl p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-semibold text-gray-900">My Print Jobs</h1>
        
        <div className="flex items-center space-x-2">
          <label htmlFor="statusFilter" className="text-sm font-medium text-gray-700">
            Filter by Status:
          </label>
          <select
            id="statusFilter"
            className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {uniqueStatuses.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Statuses' : status}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        {loading ? (
          <div className="py-8 text-gray-500 text-center">Loading...</div>
        ) : printJobs.length === 0 ? (
          <div className="py-8 text-gray-500 text-center">No print jobs found.</div>
        ) : (
          <div>
            <table className="w-full table-fixed border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">Print job ID</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">Book ID</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[16%]">Status</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">Shipping</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[7%]">Tracking</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">Est. Ship Date</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">Est. Delivery</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[14%]">Updated At</th>
                  <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((job, idx) => (
                  <tr
                    key={job.id}
                    className={idx % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50 hover:bg-gray-100'}
                  >
                    <td className="py-3 px-4 font-mono text-sm text-gray-900">{job?.printJobId || job.id}</td>
                    <td className="py-3 px-4 font-mono text-sm text-gray-900">{job.bookId}</td>
                    <td className="py-3 px-4">
                      {/* Use the effective status that prioritizes Stripe payment status */}
                      <div className="relative p-0.5">
                        {(() => {
                          // Get the print job ID
                          const printJobId = job?.printJobId || job.id;
                          // Get the detailed job data if available
                          const details = detailedJobs[printJobId];
                          // Create a combined job object with details
                          const combinedJob = { ...job, details: details };
                          // Get the effective status
                          const effectiveStatus = getEffectiveStatus(combinedJob);
                          
                          return (
                            <div className={
                              (effectiveStatus.name === 'PAID')
                                ? 'bg-blue-50 border border-blue-200 rounded-md px-3 py-2'
                                : (effectiveStatus.name === 'SHIPPED')
                                ? 'bg-green-50 border border-green-200 rounded-md px-3 py-2'
                                : (effectiveStatus.name === 'PRODUCTION_DELAYED')
                                ? 'bg-amber-50 border border-amber-200 rounded-md px-3 py-2'
                                : job.status === 'created'
                                ? 'bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2'
                                : 'bg-gray-50 border border-gray-200 rounded-md px-3 py-2'
                            }>
                              {/* Status name with better visual hierarchy */}
                              <div className={
                                (effectiveStatus.name === 'PAID')
                                  ? 'font-semibold text-blue-800 text-sm'
                                  : (effectiveStatus.name === 'SHIPPED')
                                  ? 'font-semibold text-green-800 text-sm'
                                  : (effectiveStatus.name === 'PRODUCTION_DELAYED')
                                  ? 'font-semibold text-amber-800 text-sm'
                                  : job.status === 'created'
                                  ? 'font-semibold text-yellow-800 text-sm'
                                  : 'font-semibold text-gray-700 text-sm'
                              }>
                                {effectiveStatus.name}
                              </div>
                              
                              {/* Status message with improved styling */}
                              {effectiveStatus.message && (
                                <div className={
                                  (effectiveStatus.name === 'PAID')
                                    ? 'text-xs text-blue-600 mt-1'
                                    : (effectiveStatus.name === 'SHIPPED')
                                    ? 'text-xs text-green-600 mt-1'
                                    : (effectiveStatus.name === 'PRODUCTION_DELAYED')
                                    ? 'text-xs text-amber-600 mt-1'
                                    : job.status === 'created'
                                    ? 'text-xs text-yellow-600 mt-1'
                                    : 'text-xs text-gray-500 mt-1'
                                }>
                                  {effectiveStatus.message}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {detailedJobs[job?.printJobId || job.id]?.shipping_level || '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {detailedJobs[job?.printJobId || job.id]?.line_items?.[0]?.tracking_id ? (
                        <a 
                          href={detailedJobs[job?.printJobId || job.id]?.line_items?.[0]?.tracking_urls?.[0]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Track
                        </a>
                      ) : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {detailedJobs[job?.printJobId || job.id]?.estimated_shipping_dates?.dispatch_max ? (
                        new Date(detailedJobs[job?.printJobId || job.id].estimated_shipping_dates.dispatch_max).toLocaleDateString()
                      ) : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {detailedJobs[job?.printJobId || job.id]?.estimated_shipping_dates?.arrival_max ? (
                        new Date(detailedJobs[job?.printJobId || job.id].estimated_shipping_dates.arrival_max).toLocaleDateString()
                      ) : '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{job.updatedAt?.toDate ? job.updatedAt.toDate().toLocaleDateString() : (job.createdAt?.toDate ? job.createdAt.toDate().toLocaleDateString() : '')}</td>
                    <td className="py-3 px-4 text-sm text-gray-600 text-center">
                      <Link
                        to={`/account/print-jobs/${job.id}`}
                        className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="p-4 flex items-center justify-between">
              <button
                onClick={handlePrevPage}
                disabled={loadingMore || page <= 1}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded border disabled:opacity-50"
              >
                Previous
              </button>
              <div className="text-sm text-gray-600">Page {page}</div>
              <button
                onClick={handleNextPage}
                disabled={loadingMore || !hasMore}
                className="px-3 py-2 bg-indigo-600 text-white rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal removed - using dedicated page instead */}
    </div>
  );
};

export default PrintJobsPage;
